/**
 * MetricCard - Eliza Forge Design System
 *
 * KPI display tiles with trend indicators for dashboards.
 * Built on Panel as a base container.
 */

import * as React from "react"
import { cn } from "../../shared/lib/cn"
import { Skeleton } from "./skeleton"

/* ============================================
   TYPES
   ============================================ */

type TrendDirection = "up" | "down" | "flat"

interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string
  value: string | number
  trend?: {
    direction: TrendDirection
    value: string
    favorable?: boolean
  }
  icon?: React.ReactNode
  loading?: boolean
}

interface MetricCardGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3 | 4
}

/* ============================================
   TREND ARROW
   ============================================ */

const TrendArrow: React.FC<{ direction: TrendDirection }> = ({ direction }) => {
  if (direction === "flat") {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="inline-block">
        <path d="M2 6H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  const rotation = direction === "down" ? "rotate(180 6 6)" : undefined

  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="inline-block">
      <path
        d="M6 10V2M6 2L2.5 5.5M6 2L9.5 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={rotation}
      />
    </svg>
  )
}

/* ============================================
   METRIC CARD
   ============================================ */

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  ({ className, label, value, trend, icon, loading, ...props }, ref) => {
    if (loading) {
      return (
        <div
          ref={ref}
          className={cn(
            "rounded-lg border border-gray-200 bg-white p-5",
            "dark:bg-dark-surface dark:border-dark-border",
            className
          )}
          {...props}
        >
          <Skeleton variant="text" width="40%" height={14} />
          <Skeleton variant="text" width="60%" height={28} className="mt-2" />
          <Skeleton variant="text" width="30%" height={14} className="mt-2" />
        </div>
      )
    }

    const trendColor = trend
      ? trend.direction === "flat"
        ? "text-gray-500 dark:text-gray-400"
        : trend.favorable ?? (trend.direction === "up")
          ? "text-green-600 dark:text-green-400"
          : "text-red-600 dark:text-red-400"
      : undefined

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border border-gray-200 bg-white p-5",
          "dark:bg-dark-surface dark:border-dark-border",
          className
        )}
        {...props}
      >
        <div className="flex items-center justify-between">
          <span className="text-small text-gray-500 dark:text-gray-400">{label}</span>
          {icon && (
            <span className="text-gray-400 dark:text-gray-500">{icon}</span>
          )}
        </div>

        <div className="mt-1 text-2xl font-semibold text-charcoal dark:text-gray-100">
          <span className="tabular-nums">{value}</span>
        </div>

        {trend && (
          <div className={cn("mt-1 flex items-center gap-1 text-small tabular-nums", trendColor)}>
            <TrendArrow direction={trend.direction} />
            <span>{trend.value}</span>
          </div>
        )}
      </div>
    )
  }
)
MetricCard.displayName = "MetricCard"

/* ============================================
   METRIC CARD GROUP — responsive grid wrapper
   ============================================ */

const MetricCardGroup = React.forwardRef<HTMLDivElement, MetricCardGroupProps>(
  ({ className, columns = 4, ...props }, ref) => {
    const colClass = {
      2: "md:grid-cols-2",
      3: "md:grid-cols-2 lg:grid-cols-3",
      4: "md:grid-cols-2 lg:grid-cols-4",
    }[columns]

    return (
      <div
        ref={ref}
        className={cn("grid grid-cols-1 gap-4", colClass, className)}
        {...props}
      />
    )
  }
)
MetricCardGroup.displayName = "MetricCardGroup"

export { MetricCard, MetricCardGroup }
export type { MetricCardProps, MetricCardGroupProps, TrendDirection }
